// ============================================================
// Operations Control - TypeScript Interfaces
// ============================================================

// --- Enums ---

export enum TaskStatus {
  NotStarted = 0,
  InProgress = 1,
  Paused = 2,
  Completed = 3,
  Reported = 4,
  Idle = 5,
  AbleToContinue = 6,
  Stopped = 7,
}

export type TaskStatusLabel =
  | 'Queued'
  | 'In Progress'
  | 'Paused'
  | 'Completed'
  | 'Reported'
  | 'Idle'
  | 'Resumable'
  | 'Stopped'

export const TASK_STATUS_MAP: Record<TaskStatus, { label: TaskStatusLabel; color: string }> = {
  [TaskStatus.NotStarted]: { label: 'Queued', color: '#FF9900' },
  [TaskStatus.InProgress]: { label: 'In Progress', color: '#22C55E' },
  [TaskStatus.Paused]: { label: 'Paused', color: '#F97316' },
  [TaskStatus.Completed]: { label: 'Completed', color: '#0EA5E9' },
  [TaskStatus.Reported]: { label: 'Reported', color: '#0EA5E9' },
  [TaskStatus.Idle]: { label: 'Idle', color: '#8A8078' },
  [TaskStatus.AbleToContinue]: { label: 'Resumable', color: '#F97316' },
  [TaskStatus.Stopped]: { label: 'Stopped', color: '#EF4444' },
}

export type TaskSource = 'PLAN' | 'SHIFT_PROGRAM' | 'TRIGGER' | 'PROBLEM_RESOLVER' | 'MANUAL'

export const SOURCE_CONFIG: Record<TaskSource, { label: string; color: string }> = {
  PLAN: { label: 'Plan', color: '#0EA5E9' },
  SHIFT_PROGRAM: { label: 'Shift Prog', color: '#0EA5E9' },
  TRIGGER: { label: 'Trigger', color: '#F97316' },
  PROBLEM_RESOLVER: { label: 'Resolver', color: '#EF4444' },
  MANUAL: { label: 'Manual', color: '#8A8078' },
}

export type PlanStatus = 'Active' | 'Paused' | 'Draft' | 'Completed'

export const PLAN_STATUS_COLORS: Record<PlanStatus, string> = {
  Active: '#22C55E',
  Paused: '#F97316',
  Draft: '#0EA5E9',
  Completed: '#0EA5E9',
}

export type QueueHealth = 'Stable' | 'At Risk' | 'Critical' | 'Healthy' | 'Paused'

export const QUEUE_HEALTH_COLORS: Record<QueueHealth, string> = {
  Stable: '#22C55E',
  'At Risk': '#F97316',
  Critical: '#EF4444',
  Healthy: '#22C55E',
  Paused: '#8A8078',
}

export type Priority = 'Critical' | 'High' | 'Medium' | 'Low'

export const PRIORITY_COLORS: Record<Priority, string> = {
  Critical: '#EF4444',
  High: '#F97316',
  Medium: '#FF9900',
  Low: '#8A8078',
}

export type TargetMetricType = 'Tons' | 'Cubic Meters' | 'Square Meters' | 'Count' | 'Kilometers'

export type HorizonType = 'Next Hours' | 'Next Shift'

export type DegradeRule = 'Pause' | 'Reduce' | 'Skip' | 'Alert Only'

export type AlertSeverity = 'critical' | 'warning' | 'info'

export const SEVERITY_COLORS: Record<AlertSeverity, string> = {
  critical: '#EF4444',
  warning: '#F97316',
  info: '#0EA5E9',
}

export type TriggerType = 'Time' | 'Threshold' | 'Event' | 'Completion'

export const TRIGGER_TYPE_COLORS: Record<TriggerType, string> = {
  Time: '#0EA5E9',
  Threshold: '#F97316',
  Event: '#EF4444',
  Completion: '#22C55E',
}

export type HorizonTaskState = 'inProgress' | 'assigned' | 'proposed' | 'blocked'

export const HORIZON_STATE_COLORS: Record<HorizonTaskState, { bg: string; text: string }> = {
  inProgress: { bg: '#22C55E', text: '#1E1C1A' },
  assigned: { bg: '#FF9900', text: '#1E1C1A' },
  proposed: { bg: '#3D3A35', text: '#8A8078' },
  blocked: { bg: '#EF4444', text: '#FFFFFF' },
}

// --- Interfaces ---

export interface IOperationPlan {
  id: string | number
  name: string
  siteId: number
  siteName: string
  departmentId: number
  departmentName: string
  locationScope: number[]
  priority: Priority
  targetMetricType: TargetMetricType
  targetValue: number
  targetUnit: string
  currentValue: number
  dueAt?: string
  notes?: string
  status: PlanStatus
  progress: number
  queueHealth: QueueHealth
  workers: number
  machines: number
  activeCycles: number
  estCompletion?: string
  createdAt: string
  source: 'PLAN'
  sourceId: string | number
  generatedAt: string
  cycleTemplateBindings?: ICycleTemplateBinding[]
  horizonConfig?: IHorizonConfig
}

export interface ICycleTemplateBinding {
  id: string
  taskCycleTemplateId: number
  templateName: string
  operationName: string
  stepCount: number
  components: string[]
  parallelismTarget: number
  priority: number
  degradeRule: DegradeRule
}

export interface IHorizonConfig {
  horizonType: HorizonType
  horizonValue: number
  replenishThreshold: number
  maxQueueSize: number
}

export interface IOperationalAlert {
  id: string | number
  severity: AlertSeverity
  title: string
  description: string
  timestamp: string
  actionLabel: string
  actionPath?: string
  planId?: string | number
}

export interface IActiveTask {
  id: string | number
  name: string
  machine?: string
  operator?: string
  status: TaskStatus
  statusLabel: string
  eta?: string
  location: string
  source: TaskSource
  sourceId?: string | number
  priority: Priority
  assignee?: string
  impactText?: string
  partsMissing?: boolean
}

export interface IHorizonResource {
  id: string | number
  name: string
  tasks: IHorizonTask[]
}

export interface IHorizonTask {
  id: string | number
  name: string
  state: HorizonTaskState
  durationMinutes: number
  startOffset: number
}

export interface ITeamMember {
  id: string | number
  name: string
  role: string
  status: 'busy' | 'waiting' | 'blocked' | 'available'
  currentTask?: string
  utilization: number
}

export interface IResourceStatus {
  workforce: {
    available: number
    assigned: number
    onBreak: number
    unavailable: number
  }
  machines: {
    active: number
    idle: number
    maintenance: number
    offline: number
  }
}

export interface ITrigger {
  id: string | number
  name: string
  scope: string
  type: TriggerType
  condition: string
  lastFired?: string
  nextExpected?: string
  enabled: boolean
}

export interface IShiftProgram {
  id: string | number
  name: string
  siteId: number
  siteName: string
  departmentId: number
  departmentName: string
  teamIds: number[]
  teamNames: string[]
  shiftIds: number[]
  shiftNames: string[]
  status: 'Active' | 'Paused'
  tasksPerShift: number
  workersNeeded: number
  lastRun?: string
  nextRun?: string
  taskBindings: IShiftTaskBinding[]
}

export interface IShiftTaskBinding {
  id: string
  taskTemplateId: number
  templateName: string
  operationName: string
  roleName: string
  duration: number
  machineRequired: boolean
  locationIds: number[]
  locationNames: string[]
  workersPerLocation: number
  notes?: string
}

export interface IDepartmentBoardTask extends IActiveTask {
  subtitle?: string
  sourceBadge: TaskSource
}

export interface IMachineDown {
  id: string | number
  machineName: string
  machineId: string
  downSince: string
  reason: string
  impactedPlan?: string
}

export interface IPartAtRisk {
  id: string | number
  partName: string
  forMachine: string
  orderStatus: string
}

export interface ILocationStatus {
  id: string | number
  locationName: string
  status: 'Done' | 'In Progress' | 'Queued'
  assignedWorker?: string
}

export interface IPostCoverage {
  id: string | number
  postName: string
  guardsCount: number
  coverageStatus: string
  nextHandover: string
}

export interface IKPIData {
  runningTasks: number
  blockedTasks: number
  inQueue: number
  activeWorkers: number
  activeMachines: number
}

// --- Redux State ---

export interface IOperationsSlice {
  isLoading: boolean
  kpis: IKPIData | null
  plans: IOperationPlan[]
  singlePlan: IOperationPlan | null
  alerts: IOperationalAlert[]
  activeTasks: IActiveTask[]
  horizonResources: IHorizonResource[]
  resourceStatus: IResourceStatus | null
  teamMembers: ITeamMember[]
  triggers: ITrigger[]
  shiftPrograms: IShiftProgram[]
  singleShiftProgram: IShiftProgram | null
  departmentTasks: IDepartmentBoardTask[]
  totalCount: number
  totalPages: number
}
